import Header from "../components/Header";
import Footer from "../components/Footer";
import { ArrowLeft, ArrowRight, Calendar, Clock } from "lucide-react";
import { useNavigate, useParams, Link } from "react-router-dom";

// Blog post content - all 12 articles
const blogContent = {
  "why-your-estimate-keeps-blowing-out": {
    title: "Why Your Estimate Keeps Blowing Out (And How to Fix It)",
    date: "2026-02-01",
    readTime: "5 min read",
    category: "Estimating",
    image: "https://customer-assets.emergentagent.com/job_builder-estimates/artifacts/1f491teq_IMG_1076.jpeg",
    content: `
      <p>If you've finished a job wondering where your margin went, you're not alone. It happens to good builders all the time, and it's usually not because you can't build.</p>
      
      <p>The problem is almost always in the estimate.</p>
      
      <h2>The Real Reason Estimates Fail</h2>
      
      <p>Most estimate blowouts don't come from getting the big stuff wrong. They come from the accumulation of small things that weren't included, or weren't priced properly.</p>
      
      <p>Things like:</p>
      <ul>
        <li>Temporary works and site setup</li>
        <li>Services connections and compliance</li>
        <li>Coordination time between trades</li>
        <li>Client changes that "should have been obvious"</li>
        <li>Ground conditions nobody checked</li>
      </ul>
      
      <p>None of these are glamorous. None of them are on the architectural drawings. But they all cost money, and if they're not in your estimate, they're coming out of your margin.</p>
      
      <h2>The Fix: Clear Assumptions and Boundaries</h2>
      
      <p>The single most important thing you can do is be explicit about what's in and what's out.</p>
      
      <p>Every estimate should have a clear assumptions and exclusions section. Not buried in fine print, front and centre where you and your client can both see it.</p>
      
      <p>When the client says "I thought that was included," you need to be able to point to the document and show them it wasn't.</p>
      
      <h2>Build in the Ugly Stuff</h2>
      
      <p>Stop pretending the ugly stuff doesn't exist. Every job has preliminaries, temporary works, and coordination costs. Every job has some risk.</p>
      
      <p>If you're not pricing for these things, you're subsidising your clients with your own time and money.</p>
      
      <h2>Get Someone Else to Check Your Numbers</h2>
      
      <p>When you've been staring at a job for hours, you stop seeing the gaps. Fresh eyes catch things you've missed.</p>
      
      <p>That's why estimate reviews exist, not because you're bad at estimating, but because everyone benefits from a second look.</p>
      
      <h2>The Bottom Line</h2>
      
      <p>Your estimate is a promise. If it's not accurate, you're either going to lose the job (because you priced too high) or lose money (because you priced too low).</p>
      
      <p>Neither option is good for your business.</p>
      
      <p>Take the time to get it right. Or get someone to help you do it.</p>
    `,
  },
  "materials-takeoff-vs-full-estimate": {
    title: "Materials Takeoff vs Full Estimate: Which Do You Actually Need?",
    date: "2026-01-15",
    readTime: "4 min read",
    category: "Services",
    image: "https://customer-assets.emergentagent.com/job_builder-estimates/artifacts/rocwoews_rickydee_69839_Modern_Single_level_home_minimalist_design_flo_62bc35dc-0af4-4bab-9296-e61166c9f014_3.jpeg",
    content: `
      <p>Not every job needs a full estimate. Sometimes you just need to know how much material you're dealing with.</p>
      
      <p>But knowing which output to ask for can save you time and money, and get you the information you actually need.</p>
      
      <h2>What's a Materials Takeoff?</h2>
      
      <p>A materials takeoff (or BOQ, Bill of Quantities) is exactly what it sounds like: a list of all the materials required for your project, broken down by trade.</p>
      
      <p>It gives you:</p>
      <ul>
        <li>Quantities for each material</li>
        <li>Trade-by-trade breakdown</li>
        <li>A procurement-ready list</li>
        <li>Clear assumptions about what's included</li>
      </ul>
      
      <p>What it doesn't give you is a price. You're expected to price the materials yourself, or use the quantities to get quotes from suppliers and subbies.</p>
      
      <h2>What's a Full Estimate?</h2>
      
      <p>A full estimate takes the materials takeoff and adds everything else you need to price the job: labour hours, plant and equipment, subcontract allowances, and (optionally) margin and overheads.</p>
      
      <p>It gives you:</p>
      <ul>
        <li>Materials (the BOQ)</li>
        <li>Labour hours and allowances</li>
        <li>Plant and equipment costs</li>
        <li>Subcontract allowances</li>
        <li>Clear scope boundaries</li>
      </ul>
      
      <p>The output is a price you can present to your client, or at least a solid foundation to build your price from.</p>
      
      <h2>Which One Do You Need?</h2>
      
      <p><strong>Get a materials takeoff if:</strong></p>
      <ul>
        <li>You know your labour rates and just need quantities</li>
        <li>You're shopping materials or getting subbie quotes</li>
        <li>You want to price the job yourself but need accurate quantities</li>
        <li>Budget is tight and you just need the basics</li>
      </ul>
      
      <p><strong>Get a full estimate if:</strong></p>
      <ul>
        <li>You need a complete price to present to a client</li>
        <li>You're not confident in your labour allowances</li>
        <li>The job is complex and you want a second opinion</li>
        <li>You're too busy to do the full estimate yourself</li>
      </ul>
      
      <h2>The Bottom Line</h2>
      
      <p>Both outputs have their place. The right choice depends on what you need and how much of the work you want to do yourself.</p>
      
      <p>Not sure? Send through the plans and we'll recommend the best approach for your situation.</p>
    `,
  },
  "hidden-costs-residential-builds": {
    title: "The Hidden Costs That Kill Residential Builds",
    date: "2026-01-01",
    readTime: "6 min read",
    category: "Residential",
    image: "https://customer-assets.emergentagent.com/job_builder-estimates/artifacts/4fol0kva_IMG_1579.JPG",
    content: `
      <p>Every residential builder has stories about jobs that looked profitable on paper and ended up costing them money. The culprit is almost always the same: hidden costs that weren't in the estimate.</p>
      
      <h2>The Usual Suspects</h2>
      
      <h3>1. Temporary Works</h3>
      <p>Scaffolding, propping, temporary fencing, site sheds, portaloos, these aren't free, but they're often forgotten until you need them.</p>
      
      <h3>2. Site Access and Setup</h3>
      <p>Getting materials to site, setting up laydown areas, managing traffic and neighbours. On a tight site, these costs can blow out fast.</p>
      
      <h3>3. Services Connections</h3>
      <p>Power, water, gas, telecommunications, getting services to site and connected. Often underestimated, especially on new subdivisions.</p>
      
      <h3>4. Compliance and Inspections</h3>
      <p>Building consent fees, inspection costs, compliance documentation. These add up, especially on complex builds.</p>
      
      <h3>5. Client Changes</h3>
      <p>The client wants to move a window. Simple, right? Until you factor in the knock-on effects to structure, cladding, and interior finishes.</p>
      
      <h3>6. Coordination Time</h3>
      <p>Getting trades on site at the right time, managing handovers, fixing things that weren't done right. This is real time that costs real money.</p>
      
      <h2>How to Protect Yourself</h2>
      
      <p><strong>Price explicitly for preliminaries.</strong> Don't bury them in your rates, show them as a line item so you know they're covered.</p>
      
      <p><strong>Document your assumptions.</strong> If it's not in your scope, say so. Explicitly. In writing.</p>
      
      <p><strong>Build in contingency.</strong> Not a lazy percentage, a considered allowance for the things you know will come up.</p>
      
      <p><strong>Review before you submit.</strong> Fresh eyes catch things you've missed. Use them.</p>
      
      <h2>The Bottom Line</h2>
      
      <p>Hidden costs aren't really hidden, they're just not written down. Take the time to think through the job properly, and price for what it actually takes to build it.</p>
    `,
  },
  "pricing-civil-work-drainage-earthworks": {
    title: "How to Price Civil Work Without Getting Burnt",
    date: "2025-12-15",
    readTime: "5 min read",
    category: "Civil",
    image: "https://customer-assets.emergentagent.com/job_builder-estimates/artifacts/8hafomek_IMG_3491%20%281%29.jpeg",
    content: `
      <p>Civil work has a reputation for going sideways, and it's often deserved. Drainage, earthworks, and retaining walls can turn profitable jobs into cash flow disasters faster than almost anything else.</p>
      
      <p>Here's how to price civil work without getting burnt.</p>
      
      <h2>The Big Risks</h2>
      
      <h3>Ground Conditions</h3>
      <p>Rock, water, contamination, fill, unstable ground, what's underneath determines everything. If you haven't investigated, you're guessing.</p>
      
      <h3>Weather</h3>
      <p>Civil work doesn't happen in the dry. Rain means delays, mud, and erosion control. Winter means short days and difficult conditions.</p>
      
      <h3>Access</h3>
      <p>Getting machines and materials to where they need to be. On constrained sites, this can double your costs.</p>
      
      <h3>Services</h3>
      <p>What's in the ground already? Power, water, gas, telecommunications, hit one of these and your day just got expensive.</p>
      
      <h2>How to Price It Right</h2>
      
      <p><strong>Get good information.</strong> Geotech reports, service locations, site surveys. The more you know, the less you're guessing.</p>
      
      <p><strong>Price for the conditions.</strong> If there's any uncertainty about ground conditions, price for the worst case or exclude it explicitly.</p>
      
      <p><strong>Build in float.</strong> Civil programmes slip. Build realistic duration and allow for weather and access issues.</p>
      
      <p><strong>Document exclusions.</strong> Rock excavation, dewatering, contaminated material, if you're not pricing for it, say so clearly.</p>
      
      <p><strong>Check your rates.</strong> Machine rates vary by size, operator, and conditions. Make sure your rates match the actual equipment you'll use.</p>
      
      <h2>The Bottom Line</h2>
      
      <p>Civil work rewards preparation and punishes optimism. Take the time to understand what you're pricing, and don't be afraid to build in risk.</p>
    `,
  },
  "labour-allowances-that-hold-up": {
    title: "Labour Allowances That Actually Hold Up On Site",
    date: "2025-12-01",
    readTime: "5 min read",
    category: "Labour",
    image: "https://customer-assets.emergentagent.com/job_builder-estimates/artifacts/s0jpeqfb_IMG_2977.jpeg",
    content: `
      <p>Fantasy hours from a spreadsheet don't pay your crew. If your labour allowances don't survive contact with reality, your margin won't either.</p>
      
      <h2>Why Labour Estimates Fail</h2>
      
      <p><strong>Theory vs Reality.</strong> Textbook production rates assume perfect conditions, perfect materials, and perfect coordination. Real sites have none of these.</p>
      
      <p><strong>Forgetting the Extras.</strong> Setup, cleanup, moving materials, waiting for other trades, fixing mistakes, all of this takes time that needs to be priced.</p>
      
      <p><strong>Complexity Blindness.</strong> Complex details take longer. Difficult access takes longer. Working around existing structures takes longer.</p>
      
      <h2>How to Build Better Labour Allowances</h2>
      
      <h3>Start with Reality</h3>
      <p>Use actual productivity data from jobs you've done, not theoretical rates. Track your hours and learn from them.</p>
      
      <h3>Factor in Conditions</h3>
      <p>Site access, weather exposure, complexity of work, all of these affect productivity. Build them into your rates.</p>
      
      <h3>Don't Forget Preliminaries</h3>
      <p>Site setup, tool maintenance, material handling, coordination meetings, these are real hours that need to be covered.</p>
      
      <h3>Build in Contingency</h3>
      <p>Not a lazy percentage, a considered allowance for rework, delays, and the unexpected.</p>
      
      <h3>Review and Adjust</h3>
      <p>After every job, compare your estimate to actual hours. Learn what went right and what went wrong.</p>
      
      <h2>The Bottom Line</h2>
      
      <p>Good labour allowances come from experience, not guesswork. Track your hours, learn from your jobs, and price for what it actually takes.</p>
    `,
  },
  "scope-boundaries-protect-margin": {
    title: "How Clear Scope Boundaries Protect Your Margin",
    date: "2025-11-15",
    readTime: "4 min read",
    category: "Estimating",
    image: "https://customer-assets.emergentagent.com/job_builder-estimates/artifacts/wfjc1s9h_volavechop_fireplace_covered_with_steel_sheets_in_a_modern_mini_18bfb596-61b7-4eb4-9b22-7a38c8bb56b0.png",
    content: `
      <p>"I thought that was included."</p>
      
      <p>Five words that have cost builders more money than almost anything else. And they're almost always avoidable.</p>
      
      <h2>The Problem</h2>
      
      <p>Most scope disputes don't come from bad faith, they come from ambiguity. The client assumed one thing, you assumed another, and neither of you wrote it down.</p>
      
      <p>By the time you're arguing about it, the work is usually done and the damage is already happening.</p>
      
      <h2>The Solution: Write It Down</h2>
      
      <p>Every estimate should have clear, explicit scope boundaries:</p>
      
      <ul>
        <li><strong>What's included</strong>, in enough detail that there's no confusion</li>
        <li><strong>What's excluded</strong>, especially things the client might assume are included</li>
        <li><strong>What's allowed for</strong>, provisional sums and allowances with clear limits</li>
        <li><strong>What triggers a variation</strong>, conditions that would change the price</li>
      </ul>
      
      <h2>Common Exclusions Worth Documenting</h2>
      
      <ul>
        <li>Services connections and compliance fees</li>
        <li>Council fees and inspection costs</li>
        <li>Rock or difficult ground conditions</li>
        <li>Client-supplied items and selections</li>
        <li>Work outside marked boundaries</li>
        <li>Remediation of existing issues</li>
      </ul>
      
      <h2>The Bottom Line</h2>
      
      <p>Clear scope boundaries aren't just about protecting yourself, they're about having honest conversations with your clients before the job starts, not after.</p>
      
      <p>When everyone knows what's in and what's out, there are fewer surprises and fewer disputes.</p>
    `,
  },
  "winning-work-without-underpricing": {
    title: "How to Win Work Without Underpricing It",
    date: "2025-11-01",
    readTime: "4 min read",
    category: "Business",
    image: "https://customer-assets.emergentagent.com/job_builder-estimates/artifacts/o2qiosm3_Screenshot%202026-02-12%20151040.png",
    content: `
      <p>Every builder knows the feeling. You really want the job. The client seems great. So you sharpen the pencil a bit too much, and six months later, you're wondering why you're working weekends for free.</p>
      
      <p>Here's how to win work without giving away your margin.</p>
      
      <h2>Understand Your Real Costs</h2>
      
      <p>Most underpricing happens because builders don't actually know what their jobs cost. They know the materials. They know the subbie quotes. But they forget the hours they spend on site, the phone calls, the coordination, the rework.</p>
      
      <p>Track your time. Track your actual costs. Know your numbers, not just the obvious ones.</p>
      
      <h2>Price for Value, Not Just Cost</h2>
      
      <p>You're not selling hours and materials. You're selling a finished result, delivered on time, without drama. That has value.</p>
      
      <p>Clients who only care about the cheapest price are rarely the clients you want. The good ones understand that quality and reliability cost money.</p>
      
      <h2>Qualify Your Leads</h2>
      
      <p>Not every enquiry deserves a full estimate. Before you spend hours pricing a job, find out:</p>
      
      <ul>
        <li>Is the budget realistic for what they want?</li>
        <li>Are they ready to proceed, or just shopping?</li>
        <li>Do they understand what's involved?</li>
        <li>Are there red flags you should pay attention to?</li>
      </ul>
      
      <p>It's better to say no early than to win a job that was never going to work.</p>
      
      <h2>Present Your Estimate Professionally</h2>
      
      <p>A clear, professional estimate builds confidence. It shows you've thought the job through. It demonstrates you know what you're doing.</p>
      
      <p>Clients will pay more for a builder they trust. Your estimate is part of building that trust.</p>
      
      <h2>The Bottom Line</h2>
      
      <p>You can be competitive and profitable. But it starts with knowing your numbers, pricing for reality, and being willing to walk away from jobs that don't stack up.</p>
    `,
  },
  "simple-programme-every-builder-needs": {
    title: "The Simple Programme Every Builder Needs (But Rarely Has)",
    date: "2025-10-15",
    readTime: "5 min read",
    category: "Programme",
    image: "https://customer-assets.emergentagent.com/job_builder-estimates/artifacts/nsk0trd0_IMG_3006%20%281%29.jpeg",
    content: `
      <p>You don't need MS Project. You don't need a Gantt chart that takes three screens to display. What you need is a clear sequence that shows what happens when, and what's holding everything up.</p>
      
      <h2>Why Most Builders Don't Programme</h2>
      
      <p>It feels like overhead. You know the job, you know the sequence, it's all in your head. Why write it down?</p>
      
      <p>Because your head isn't a reliable filing system. Because things change. Because when three trades are waiting and the client is asking questions, you need something you can point to.</p>
      
      <h2>What a Simple Programme Looks Like</h2>
      
      <p>A useful programme doesn't need to be complicated. It needs to answer:</p>
      
      <ul>
        <li><strong>What's the sequence?</strong> What needs to happen before what?</li>
        <li><strong>What are the durations?</strong> How long will each phase take?</li>
        <li><strong>What are the dependencies?</strong> What's waiting on what?</li>
        <li><strong>What are the lead times?</strong> What needs to be ordered early?</li>
        <li><strong>What are the constraints?</strong> Inspections, weather, client decisions?</li>
      </ul>
      
      <p>That's it. A list, a timeline, and the critical dependencies. It doesn't need to be fancy.</p>
      
      <h2>The Benefits</h2>
      
      <p><strong>Better coordination.</strong> Trades know when they're needed. Materials arrive when you need them.</p>
      
      <p><strong>Earlier problem detection.</strong> You see the bottleneck coming before it arrives.</p>
      
      <p><strong>Client communication.</strong> When they ask how it's going, you have something to show them.</p>
      
      <p><strong>Variation support.</strong> When things change, you can show the impact on the programme.</p>
      
      <h2>The Bottom Line</h2>
      
      <p>A simple programme takes a few hours to build and saves days of chaos. It's one of the highest-value things you can do before a job starts.</p>
    `,
  },
  "estimating-renovations-extensions": {
    title: "Why Renovations and Extensions Are Harder to Estimate",
    date: "2025-10-01",
    readTime: "5 min read",
    category: "Residential",
    image: "https://customer-assets.emergentagent.com/job_builder-estimates/artifacts/wq9mb8f0_IMG_3050%20%281%29.jpeg",
    content: `
      <p>New builds are predictable. You start with a clean site, you follow the drawings, you know what's coming. Renovations have surprises hiding behind every wall.</p>
      
      <p>Here's why renovation estimates are harder, and how to price them properly.</p>
      
      <h2>The Unknown Unknowns</h2>
      
      <p>With a new build, you know what you're getting. With a renovation, you're working with whatever the last guy did, and the guy before that, and the guy before that.</p>
      
      <p>Common surprises:</p>
      <ul>
        <li>Framing that doesn't match the drawings (or any drawings)</li>
        <li>Hidden rot, rust, or water damage</li>
        <li>Asbestos, lead paint, or other nasties</li>
        <li>Services in unexpected places</li>
        <li>Previous "repairs" that need to be undone</li>
      </ul>
      
      <p>You can't price what you can't see. And with renovations, there's always something you can't see.</p>
      
      <h2>How to Price for Uncertainty</h2>
      
      <h3>Investigate First</h3>
      <p>Before you commit to a price, do as much investigation as you can. Open up walls, check the roof space, look at the subfloor. The more you know, the less you're guessing.</p>
      
      <h3>Document Your Assumptions</h3>
      <p>Be explicit about what you're assuming. "Price assumes existing framing is sound." "Price assumes no asbestos." If the assumption turns out to be wrong, you have grounds for a variation.</p>
      
      <h3>Use Provisional Sums</h3>
      <p>For areas of genuine uncertainty, use provisional sums with clear scope. "Provisional sum of $X for subfloor repairs, to be confirmed on opening up."</p>
      
      <h3>Build in Contingency</h3>
      <p>Renovations need more contingency than new builds. How much depends on the age of the building, the extent of the work, and how much investigation you've been able to do.</p>
      
      <h2>The Bottom Line</h2>
      
      <p>Renovation estimates are harder because there's more uncertainty. The solution isn't to guess lower, it's to investigate more, document clearly, and price for the risk you're taking on.</p>
    `,
  },
  "boq-format-that-works": {
    title: "The BOQ Format That Actually Works for Builders",
    date: "2025-09-15",
    readTime: "4 min read",
    category: "BOQ",
    image: "https://customer-assets.emergentagent.com/job_builder-estimates/artifacts/zawnz8f6_IMG_3087.JPG",
    content: `
      <p>Fancy QS formats look impressive. Elemental breakdowns, NRM codes, detailed work sections. But if you can't use them to order materials or get subbie quotes, what's the point?</p>
      
      <p>Here's what a builder-friendly BOQ actually looks like.</p>
      
      <h2>What Builders Actually Need</h2>
      
      <p>A BOQ is useful if it helps you:</p>
      <ul>
        <li>Order materials from suppliers</li>
        <li>Get quotes from subbies</li>
        <li>Track quantities during the job</li>
        <li>Identify what's included (and what's not)</li>
      </ul>
      
      <p>That means the format needs to match how you actually buy and manage the work.</p>
      
      <h2>Trade-Based, Not Elemental</h2>
      
      <p>QS formats often break things down by element, foundations, superstructure, finishes. That's great for cost planning, but useless for procurement.</p>
      
      <p>Builders need trade-based breakdowns:</p>
      <ul>
        <li>Concrete and formwork</li>
        <li>Framing and carpentry</li>
        <li>Roofing</li>
        <li>Plumbing</li>
        <li>Electrical</li>
      </ul>
      
      <p>This matches how you hire subbies and order materials.</p>
      
      <h2>Clear Descriptions</h2>
      
      <p>Every item should describe:</p>
      <ul>
        <li>What it is (material, size, specification)</li>
        <li>How much (quantity, unit of measure)</li>
        <li>Where it goes (location or area)</li>
      </ul>
      
      <p>Vague descriptions cause confusion. Specific descriptions get accurate quotes.</p>
      
      <h2>Assumptions Listed</h2>
      
      <p>What's included? What's excluded? What's allowed for but not confirmed? Every BOQ should have a clear assumptions section so everyone knows the boundaries.</p>
      
      <h2>The Bottom Line</h2>
      
      <p>A good BOQ is a tool, not a document. If it helps you buy materials and manage subbies, it's working. If it just looks impressive on paper, it's not.</p>
    `,
  },
  "ground-conditions-contingency": {
    title: "Ground Conditions: The Contingency Nobody Budgets For",
    date: "2025-09-01",
    readTime: "5 min read",
    category: "Civil",
    image: "https://customer-assets.emergentagent.com/job_builder-estimates/artifacts/2s8djtid_Screenshot%202026-02-12%20151009.png",
    content: `
      <p>Rock, water, clay, fill, ground conditions can turn a profitable job into a cashflow nightmare overnight. And yet most estimates treat the ground as an afterthought.</p>
      
      <h2>Why Ground Conditions Matter</h2>
      
      <p>Everything sits on the ground. Foundations, drainage, services, retaining walls. If the ground isn't what you expected, everything changes:</p>
      
      <ul>
        <li><strong>Rock</strong>, Excavation costs multiply. Foundations may need redesign.</li>
        <li><strong>Water</strong>, Dewatering, tanking, drainage. All expensive.</li>
        <li><strong>Poor ground</strong>, Over-excavation, imported fill, engineered solutions.</li>
        <li><strong>Contamination</strong>, Disposal costs, testing, remediation.</li>
      </ul>
      
      <p>These aren't small variations. They can blow your budget completely.</p>
      
      <h2>What You Can Do</h2>
      
      <h3>Get Information Early</h3>
      <p>Geotech reports aren't cheap, but they're cheaper than discovering rock halfway through excavation. For any significant job, investigate before you price.</p>
      
      <h3>Price for What You Know</h3>
      <p>If you have geotech, price for what it says. If you don't, price for normal conditions and exclude the rest.</p>
      
      <h3>Exclude Clearly</h3>
      <p>"Price excludes rock excavation." "Price excludes dewatering." "Price assumes stable, free-draining ground." Put it in writing.</p>
      
      <h3>Build in Contingency</h3>
      <p>Even with good information, there are surprises. Allow for them. A considered contingency is better than a hopeful estimate.</p>
      
      <h3>Variation Process</h3>
      <p>When conditions change, document it immediately. Photos, records, costs. You need evidence to claim variations.</p>
      
      <h2>The Bottom Line</h2>
      
      <p>Ground conditions are one of the biggest risks in construction. Don't ignore them. Investigate, exclude, and allow for the unexpected.</p>
    `,
  },
  "when-to-say-no-to-a-job": {
    title: "When to Say No to a Job (Before It Costs You)",
    date: "2025-08-15",
    readTime: "4 min read",
    category: "Business",
    image: "https://customer-assets.emergentagent.com/job_builder-estimates/artifacts/rw8whfg7_rickydee_69839_Extend_deck_to_edge._Hyper_Realistic._7207206f-fb46-414d-a0a4-ce5fd11eeaa7.png",
    content: `
      <p>Not every job is worth winning. Some jobs look good on paper but cost you money. Some clients seem great until they're not. The hardest skill in building isn't doing the work, it's knowing which work to walk away from.</p>
      
      <h2>Red Flags to Watch For</h2>
      
      <h3>Unrealistic Budget</h3>
      <p>If the budget doesn't match the scope, someone's going to be disappointed. Better to find out now than halfway through the job.</p>
      
      <h3>Unrealistic Timeline</h3>
      <p>"Can you start Monday?" on a job that needs proper planning is a warning sign. Rush jobs rarely end well.</p>
      
      <h3>Difficult History</h3>
      <p>Ask around. If three builders have already walked off this job, there's probably a reason.</p>
      
      <h3>Scope Creep in the Enquiry</h3>
      <p>If the scope keeps changing before you've even quoted, imagine what it'll be like during the job.</p>
      
      <h3>Price Shopping</h3>
      <p>Clients who only care about getting the cheapest quote will grind you down. They'll question every variation and expect extras for free.</p>
      
      <h3>Bad Gut Feeling</h3>
      <p>Trust your instincts. If something feels off, it probably is.</p>
      
      <h2>How to Say No</h2>
      
      <p>You don't need to explain yourself. A simple "thanks for thinking of us, but we're not the right fit for this one" is enough.</p>
      
      <p>If you want to be helpful, you can point them towards someone else. But you don't owe anyone a detailed explanation of why you're walking away.</p>
      
      <h2>The Cost of Saying Yes</h2>
      
      <p>Every bad job you take is a good job you can't do. Every difficult client burns time and energy you could spend elsewhere. Every underpriced project drains your resources.</p>
      
      <p>Saying no to the wrong work creates space for the right work.</p>
      
      <h2>The Bottom Line</h2>
      
      <p>Not every enquiry deserves a quote. Not every quote deserves to win. The best builders aren't the ones who take every job, they're the ones who take the right jobs.</p>
    `,
  },
};

// Default content for any posts without detailed content
const defaultContent = `
  <p>This article is coming soon. Check back later for the full content.</p>
  
  <p>In the meantime, if you have questions about this topic, get in touch. We're always happy to talk through your specific situation.</p>
`;

export default function BlogPostPage() {
  const navigate = useNavigate();
  const { slug } = useParams();

  const scrollToContact = () => {
    navigate("/#contact");
  };

  const post = blogContent[slug];
  
  if (!post) {
    return (
      <div className="min-h-screen bg-white">
        <Header onContactClick={scrollToContact} />
        <main className="pt-32 pb-24">
          <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
            <h1 className="font-heading font-bold text-4xl uppercase mb-6">
              Post Not Found
            </h1>
            <p className="font-body text-zinc-600 mb-8">
              The blog post you're looking for doesn't exist.
            </p>
            <Link
              to="/blog"
              className="inline-flex items-center gap-2 font-mono text-sm uppercase tracking-widest hover:gap-4 transition-all"
            >
              <ArrowLeft size={16} />
              Back to Blog
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-NZ', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  return (
    <div className="min-h-screen bg-white">
      <Header onContactClick={scrollToContact} />
      
      <main className="pt-20 md:pt-24">
        {/* Hero Image */}
        <section className="relative h-64 md:h-96">
          <img
            src={post.image}
            alt={post.title}
            className="w-full h-full object-cover grayscale"
          />
          <div className="absolute inset-0 bg-black/40" />
        </section>

        {/* Article */}
        <article className="py-12 md:py-16">
          <div className="max-w-3xl mx-auto px-6 md:px-12">
            {/* Back link */}
            <Link
              to="/blog"
              className="inline-flex items-center gap-2 font-mono text-sm uppercase tracking-widest text-zinc-500 hover:text-black hover:gap-4 transition-all mb-8"
            >
              <ArrowLeft size={16} />
              Back to Blog
            </Link>

            {/* Meta */}
            <div className="flex flex-wrap items-center gap-4 mb-6">
              <span className="font-mono text-xs uppercase tracking-widest bg-black text-white px-3 py-1">
                {post.category}
              </span>
              <span className="font-mono text-xs text-zinc-500 flex items-center gap-1">
                <Calendar size={12} />
                {formatDate(post.date)}
              </span>
              <span className="font-mono text-xs text-zinc-500 flex items-center gap-1">
                <Clock size={12} />
                {post.readTime}
              </span>
            </div>

            {/* Title */}
            <h1 className="font-heading font-bold text-2xl sm:text-3xl md:text-4xl uppercase mb-8 leading-tight">
              {post.title}
            </h1>

            {/* Content */}
            <div 
              className="prose prose-lg max-w-none font-body
                prose-headings:font-heading prose-headings:uppercase prose-headings:tracking-tight
                prose-h2:text-xl prose-h2:md:text-2xl prose-h2:mt-10 prose-h2:mb-4
                prose-h3:text-lg prose-h3:md:text-xl prose-h3:mt-6 prose-h3:mb-3
                prose-p:text-zinc-700 prose-p:leading-relaxed prose-p:mb-4
                prose-ul:my-4 prose-ul:pl-0 prose-li:text-zinc-700 prose-li:my-2 prose-li:pl-0
                prose-strong:text-black prose-strong:font-semibold"
              dangerouslySetInnerHTML={{ __html: post.content || defaultContent }}
            />

            {/* CTA */}
            <div className="mt-16 pt-12 border-t-4 border-black">
              <h2 className="font-heading font-bold text-2xl uppercase mb-4">
                Need Help With Your Estimate?
              </h2>
              <p className="font-body text-zinc-600 mb-6">
                Send through the plans and we'll come back with scope, fee, and turnaround.
              </p>
              <button
                onClick={scrollToContact}
                className="bg-black text-white h-12 px-6 uppercase font-bold tracking-widest border-2 border-black btn-brutal inline-flex items-center gap-2"
              >
                Get A Quote
                <ArrowRight size={16} />
              </button>
            </div>
          </div>
        </article>
      </main>

      <Footer />
    </div>
  );
}
